import { Fragment, useContext, useState, useEffect, useMemo } from "react";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";

import {
  Box,
  Flex,
  useToast,
  useColorMode,
  useDisclosure,
} from "@chakra-ui/react";

import { useQuery, useMutation, useApolloClient } from "@apollo/client";
import AuthContext from "../contexts/AuthContext";
import { useParams } from "react-router";
import GetStudentProgress from "../graphql/queries/progress"; // Add this import
import Subject from "../graphql/queries/subject";
import ManageEnrollSubjectPageQuery from "../graphql/queries/manageEnrollSubjectPage";
import updateStudentOfSubject from "../graphql/mutations/updateStudentOfSubject";
import studentofsubjectsRemoveMutation from "../graphql/mutations/removeStudentofSubject";

import StudentListPanel from "../components/pages/ManageEnrollSubjectPage/StudentListPanel";
import DashboardDetail from "../components/pages/ManageEnrollSubjectPage/DashboardDetail";
import DialogCertificate from "../components/pages/ManageEnrollSubjectPage/DialogCertificate";
import MenuDashboard from "../components/pages/ManageEnrollSubjectPage/MenuDashboard";
import DashboardUser from "../components/pages/ManageEnrollSubjectPage/DashboardUser";
import DialogUnEnroll from "../components/pages/ManageEnrollSubjectPage/DialogUnEnroll";
import axios from "axios";

const API_BASE_URL = (
  process.env.REACT_APP_URL_PUBLIC ||
  (process.env.REACT_APP_GRAPHQL_SERVER || "").replace(/\/graphql\/?$/, "")
).replace(/\/$/, "");
const URL_DASHBOARD = process.env.REACT_APP_DASHBOARD_API || `${API_BASE_URL}/dashboard`;

const getCertificateProgressValue = (progressEntry = {}) => {
  const progress = Number(progressEntry.progress) || 0;
  const zoomprogress = Number(progressEntry.zoomprogress) || 0;
  return progressEntry.certificateProgress ?? Math.max(progress, zoomprogress);
};

const ManageEnrollSubjectPage = () => {
  const { t } = useTranslation();
  const { user } = useContext(AuthContext);
  const { colorMode } = useColorMode();
  const params = useParams();
  const history = useHistory();
  const toast = useToast();
  const client = useApolloClient();

  const [dashboard, setDashboard] = useState(null);
  const [dashboardError, setDashboardError] = useState("");
  const [dashboardRefreshKey, setDashboardRefreshKey] = useState(0);
  const [isStudentPanelOpen, setIsStudentPanelOpen] = useState(false);
  const [dashboardUserLoading, setDashboardUserLoading] = useState(false);
  const [studentPage, setStudentPage] = useState(1);
  const [studentsPerPage, setStudentsPerPage] = useState(25);

  const {
    isOpen: unEnrollSubject,
    onClose: closeUnEnrollSubject,
  } = useDisclosure();
  // const { isOpen: studentListDialog, onOpen: openStudentListDialog, onClose: closeStudentListDialog, } = useDisclosure()
  const {
    isOpen: certificateDialog,
    onOpen: openCertificateDialog,
    onClose: closeCertificateDialog,
  } = useDisclosure();

  const [setstudentofsubjectsRemoveMutation] = useMutation(
    studentofsubjectsRemoveMutation
  );
  const [setUpdateStudentOfSubject] = useMutation(updateStudentOfSubject);
  const [student, setStudent] = useState();
  const [progress, setProgress] = useState([]);
  const [loading, setLoading] = useState(false); // This is for the axios dashboard call

  const {
    data: SubjectData,
    loading: subjectQueryLoading,
    refetch: refetchSubject,
  } = useQuery(Subject, {
    variables: {
      _id: params?.subjectId,
    },
    fetchPolicy: "network-only",
  });

  const {
    data: studentEnrollPageData,
    loading: studentEnrollPageLoading,
    refetch: refetchEnrollPage,
  } = useQuery(ManageEnrollSubjectPageQuery, {
    variables: {
      subject: params?.subjectId,
      page: studentPage,
      perPage: studentsPerPage,
    },
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
    skip: !params?.subjectId || !isStudentPanelOpen,
  });

  const enrollmentPage = studentEnrollPageData?.manageEnrollSubjectPage;
  const studentList = enrollmentPage?.items || [];

  const processedProgressData = useMemo(() => {
    const result = new Map();

    (enrollmentPage?.progresses || []).forEach((progressEntry) => {
      const studentId = progressEntry.studentId;
      const contentId = progressEntry.contentId;
      if (!studentId || !contentId) return;

      const studentProgress = result.get(studentId) || new Map();
      studentProgress.set(contentId, {
        _id: progressEntry._id,
        progress: Number(progressEntry.progress) || 0,
        zoomprogress: Number(progressEntry.zoomprogress) || 0,
        certificateProgress: getCertificateProgressValue(progressEntry),
        original_item: progressEntry,
      });
      result.set(studentId, studentProgress);
    });

    return result;
  }, [enrollmentPage]);

  const contentQuestionsMap = useMemo(() => {
    const result = new Map();

    (SubjectData?.subject?.contents || []).forEach((content) => {
      if (!content?._id) return;
      const questions =
        content.type === "quiz"
          ? content.questionconfig?.questionList
          : content.type === "survey"
            ? content.surveyconfig?.surveyQuestions?.questions
            : [];
      result.set(content._id, Array.isArray(questions) ? questions : []);
    });

    return result;
  }, [SubjectData]);

  useEffect(() => {
    setStudentPage(1);
  }, [params?.subjectId]);

  useEffect(() => {
    if (enrollmentPage?.totalPages && studentPage > enrollmentPage.totalPages) {
      setStudentPage(enrollmentPage.totalPages);
    }
  }, [enrollmentPage, studentPage]);

  const handleSubmitUnEnroll = async (e) => {
    e.preventDefault();
    try {
      const { data: responseUnEnroll } =
        await setstudentofsubjectsRemoveMutation({
          variables: {
            _id: student?._id,
          },
        });
      if (responseUnEnroll) {
        await toast({
          position: "top",
          title: "Success",
          description: `${t("ManageEnrollSubjectPage.toastSuccess")}`,
          status: "success",
          duration: 3000,
          isClosable: true,
        });
        closeUnEnrollSubject();
        // closeStudentListDialog() // DialogListStudent is removed
        setIsStudentPanelOpen(false); // Close panel if open after unenroll
        refetchPage();
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleUpdateCertificateStatus = async (e) => {
    e.preventDefault();
    // console.log("CERTIFICATE", student)
    try {
      const { data: approvedStatus } = await setUpdateStudentOfSubject({
        variables: {
          _id: student?._id,
          status: student?.status === "disapproved" ? "approved" : "disapproved",
        },
      });
      if (approvedStatus) {
        await toast({
          position: "top",
          title: "Success",
          description: `${t("ManageEnrollSubjectPage.update_certificate")}`,
          status: "success",
          duration: 3000,
          isClosable: true,
        });
        closeCertificateDialog();
        refetchPage();
      }
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    if (user?.role === "STUDENT") {
      setTimeout(() => {
        history.push("/login");
      }, 100);
    }
  }, [history, user?.role]);

  // Define event handlers for student selection and panel toggle
  const showMainDashboard = () => {
    setStudent(null);
    setIsStudentPanelOpen(false);
    setDashboardRefreshKey((current) => current + 1);
  };

  const viewStudentDetail = async (selectedStudentItem) => {
    if (
      !selectedStudentItem ||
      !selectedStudentItem.student ||
      !selectedStudentItem.student._id
    ) {
      console.error(
        "Invalid student item for viewing details:",
        selectedStudentItem
      );
      setStudent(null);
      setProgress([]);
      setIsStudentPanelOpen(false);
      setDashboardUserLoading(false); // Ensure loading is off if bailing early
      return;
    }

    setStudent(selectedStudentItem);
    setIsStudentPanelOpen(false);
    setDashboardUserLoading(true);
    setProgress([]); // Clear previous student's progress

    try {
      const rawProgressQueryResult = await client.query({
        query: GetStudentProgress,
        variables: { student: selectedStudentItem.student._id },
        fetchPolicy: "network-only",
      });

      console.log("Raw progress query result:", rawProgressQueryResult); // Log the raw result

      const { data: progressDataResult, error: progressError } =
        rawProgressQueryResult || {}; // Safeguard destructuring

      if (progressError) {
        // ... (rest of the error handling remains the same)
        console.error(
          "Error fetching progress for DashboardUser:",
          progressError
        );
        toast({
          title: t(
            "ManageEnrollSubjectPage.error_fetching_student_progress_title",
            "Error Fetching Student Progress"
          ),
          description:
            progressError.message ||
            t(
              "ManageEnrollSubjectPage.error_fetching_student_progress_desc",
              "Could not load progress for the selected student."
            ),
          status: "error",
          duration: 5000,
          isClosable: true,
        });
        setProgress([]);
      } else if (progressDataResult && progressDataResult.progresss) {
        const allFetchedProgressItems = progressDataResult.progresss;
        const validFormattedProgress = [];
        const problematicProgressItems = [];

        allFetchedProgressItems.forEach((p) => {
          const contentId = p.content?._id;
          const progressValue = getCertificateProgressValue(p);

          if (contentId && progressValue !== undefined) {
            validFormattedProgress.push({
              _id: p._id,
              content: contentId,
              progress: progressValue,
              zoomprogress: p.zoomprogress || 0,
              original_item: p,
            });
          } else {
            problematicProgressItems.push({
              original_item: p,
              extracted_content_id: contentId,
              extracted_progress: progressValue,
            });
          }
        });

        if (problematicProgressItems.length > 0) {
          // console.warn("Problematic progress items found:", problematicProgressItems);
        }
        setProgress(validFormattedProgress);
      } else {
        // This case might be hit if rawProgressQueryResult is null/undefined leading to {}
        // or if data is null or progresss is null within data.
        console.warn(
          "No progress data or unexpected structure in progressDataResult for DashboardUser:",
          progressDataResult
        );
        setProgress([]); // No progress data found
      }
    } catch (e) {
      console.error("Exception during viewStudentDetail progress fetch:", e);
      toast({
        title: t(
          "ManageEnrollSubjectPage.error_unexpected_title",
          "Unexpected Error"
        ),
        description:
          e.message ||
          t(
            "ManageEnrollSubjectPage.error_fetching_student_progress_unexpected_desc",
            "An unexpected error occurred while fetching student progress."
          ),
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      setProgress([]);
    } finally {
      setDashboardUserLoading(false);
    }
  };

  const toggleStudentPanelDisplay = () => {
    if (!isStudentPanelOpen && student) {
      // If panel is about to open and a student is selected
      setStudent(null); // Clear student view to show the list
    }
    setIsStudentPanelOpen(!isStudentPanelOpen);
  };

  // Corrected useEffect for axios
  useEffect(() => {
    let isCurrentRequest = true;

    const fetchDashboardDetailData = async () => {
      if (!student && !isStudentPanelOpen) {
        setLoading(true);
        setDashboardError("");
        try {
          const response = await axios.post(
            `${URL_DASHBOARD}/${params?.subjectId}`
          );
          if (isCurrentRequest && response.data && !Array.isArray(response.data)) {
            setDashboard(response.data);
          } else if (isCurrentRequest) {
            throw new Error("Dashboard API returned an invalid response");
          }
        } catch (error) {
          if (isCurrentRequest) {
            setDashboard(null);
            setDashboardError(
              error.response?.data?.message || error.message || "ไม่สามารถโหลดข้อมูลภาพรวมได้"
            );
          }
        } finally {
          if (isCurrentRequest) setLoading(false);
        }
      } else {
        setLoading(false);
      }
    };

    if (params?.subjectId) fetchDashboardDetailData();

    return () => {
      isCurrentRequest = false;
    };
  }, [
    student,
    isStudentPanelOpen,
    params?.subjectId,
    dashboardRefreshKey,
  ]);

  const refetchPage = () => {
    refetchSubject({
      _id: params?.subjectId,
    });
    if (isStudentPanelOpen) {
      refetchEnrollPage({
        subject: params?.subjectId,
        page: studentPage,
        perPage: studentsPerPage,
      });
    }
  };

  return (
    <Fragment>
      <Box w="100%" h="auto" sx={{ overflowX: ["auto", "auto", "none"] }}>
        <Flex
          minHeight={"90vh"}
          templateRows="repeat(2, 1fr)"
          templateColumns="repeat(12, 1fr)"
          gap={0}
          direction={["column", "column", "row", "row"]}
        >
          <MenuDashboard
            onShowDashboard={showMainDashboard}
            toggleStudentPanel={toggleStudentPanelDisplay}
            SubjectData={SubjectData}
            subjectId={params?.subjectId}
          />
          <Box
            flex="1"
            px={["2", "5", "5", "10"]}
            bg={colorMode === "light" ? "gray.100" : "brand.700"}
            sx={{ overflowX: ["auto", "auto", "none"] }}
          >
            {isStudentPanelOpen ? (
              <StudentListPanel
                studentList={studentList}
                contentList={SubjectData?.subject?.contents || []}
                progressData={processedProgressData}
                contentQuestionsMap={contentQuestionsMap}
                isLoading={subjectQueryLoading || (studentEnrollPageLoading && !enrollmentPage)}
                isPageLoading={studentEnrollPageLoading}
                setStudent={viewStudentDetail}
                currentSubjectId={params?.subjectId}
                page={enrollmentPage?.page || studentPage}
                perPage={enrollmentPage?.perPage || studentsPerPage}
                total={enrollmentPage?.total || 0}
                totalPages={enrollmentPage?.totalPages || 1}
                hasNextPage={enrollmentPage?.hasNextPage || false}
                hasPrevPage={enrollmentPage?.hasPrevPage || false}
                onPageChange={setStudentPage}
                onPerPageChange={(value) => {
                  setStudentsPerPage(value);
                  setStudentPage(1);
                }}
                onZoomProgressImported={refetchPage}
                onProgressCleared={refetchPage}
                onStudentsImported={refetchPage}
              />
            ) : student ? (
              <DashboardUser
                loading={dashboardUserLoading}
                student={student}
                SubjectData={SubjectData}
                progressList={progress}
                openCertificateDialog={openCertificateDialog}
              />
            ) : (
              <DashboardDetail
                loading={loading}
                error={dashboardError}
                total={dashboard?.dashboard_total}
                pass={dashboard?.dashboard_pass}
                notpass={dashboard?.dashboard_notpass}
                certificate={dashboard?.dashboard_certificate}
                credit={dashboard?.dashboard_credit}
                dashboardContent={dashboard?.content}
              />
            )}
          </Box>
        </Flex>
      </Box>

      {/* <DialogListStudent
                setLoading={setLoading}
                contents={SubjectData?.subject?.contents}
                setProgress={setProgress}
                setStudent={setStudent}
                studentList={StudentEnroll?.studentofsubjects}
                studentListDialog={studentListDialog}
                closeStudentListDialog={closeStudentListDialog}
                openUnEnrollSubject={openUnEnrollSubject} /> */}

      <DialogCertificate
        student={student}
        handleUpdateCertificateStatus={handleUpdateCertificateStatus}
        certificateDialog={certificateDialog}
        closeCertificateDialog={closeCertificateDialog}
      />

      <DialogUnEnroll
        student={student}
        unEnrollSubject={unEnrollSubject}
        closeUnEnrollSubject={closeUnEnrollSubject}
        handleSubmitUnEnroll={handleSubmitUnEnroll}
      />
    </Fragment>
  );
};

export default ManageEnrollSubjectPage;
